import {ITestheftAPI, ITestheftAPI_aufgabe, ITestheftAPI_modul} from './Interfaces';
import {IDidaktische_kommentare_aufgabenDB, IDomaenenDB} from './InterfacesDB';
import TooltipBase from './TooltipBase';
import {getTooltipMaterialien, renderTooltipDatenhinweis} from './TooltipMaterialien';
import Utilities from './Utilities';

const AUFGABENBILD_BASISPFAD = '/materialien/vera5/aufgaben-bilder';
const BASE_AUFG_ID_PATTERN = /^26v5[a-z0-9_]+$/;
const BOOTSTRAP_ICON_SPRITE = '/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg';

/** Bündelt eine Aufgabe mit ihrem fachlichen Kontext im Testheft */
interface ITooltipAufgabeData {
    testheft: ITestheftAPI;
    modul: ITestheftAPI_modul;
    aufgabe: ITestheftAPI_aufgabe;
}

/** Zeigt zu einer mit `data-tooltip-aufgabe-id` markierten Aufgabenbezeichnung zusätzliche Aufgabeninformationen an */
export default class TooltipAufgabe extends TooltipBase {
    private readonly aufgabeIndex: ReadonlyMap<string, ITooltipAufgabeData>; // Zugriff auf Aufgabendaten im konkreten Testheftkontext
    private readonly kommentarIndex: ReadonlyMap<number, IDidaktische_kommentare_aufgabenDB>; // didaktische Zusatzinformationen über die Aufgabe-ID

    /** Bereitet Aufgabendaten, Popover, Tooltip-Auslöser und Benutzerinteraktionen vor */
    public constructor(testhefte: readonly ITestheftAPI[], kommentare: readonly IDidaktische_kommentare_aufgabenDB[], private readonly domaenen: readonly IDomaenenDB[], private readonly projektweiche: string) {
        super({
            id: 'tooltip-aufgabe',
            cssKlasse: 'tooltip-aufgabe',
            inhaltCssKlasse: 'tooltip-aufgabe-content',
            ariaLabel: 'Aufgabeninformationen',
            triggerSelektor: 'span[data-tooltip-aufgabe-id]',
            triggerAttribute: ['data-tooltip-aufgabe-id', 'data-tooltip-testheft-id']
        });

        // Aufgabeninformationen einmal aufbereiten, damit sie beim Öffnen nicht erneut gesucht werden müssen
        this.aufgabeIndex = this.createAufgabeIndex(testhefte);
        this.kommentarIndex = this.createKommentarIndex(kommentare);
    }

    /** Erstellt ein Nachschlagewerk, das jede Aufgabe mit ihrem Testheft und Modul verbindet */
    private createAufgabeIndex(testhefte: readonly ITestheftAPI[]): ReadonlyMap<string, ITooltipAufgabeData> {
        const aufgabeIndex = new Map<string, ITooltipAufgabeData>();

        // Die Teststruktur von außen nach innen bis zu den einzelnen Aufgaben durchlaufen
        testhefte.forEach(testheft => {
            testheft.module.forEach(modul => {
                modul.aufgaben.forEach(aufgabe => {
                    aufgabeIndex.set(this.createIndexKey(testheft.testheft_id, aufgabe.aufgabe_id), {testheft, modul, aufgabe});
                });
            });
        });

        return aufgabeIndex;
    }

    /** Erstellt ein Nachschlagewerk für die didaktischen Zusatzinformationen */
    private createKommentarIndex(kommentare: readonly IDidaktische_kommentare_aufgabenDB[]): ReadonlyMap<number, IDidaktische_kommentare_aufgabenDB> {
        const kommentarIndex = new Map<number, IDidaktische_kommentare_aufgabenDB>();
        kommentare.forEach(kommentar => {
            if (kommentar.aufgabe_id !== undefined)
                kommentarIndex.set(kommentar.aufgabe_id, kommentar);
        });

        return kommentarIndex;
    }

    /** Verbindet Testheft und Aufgabe zu einem eindeutigen Indexschlüssel */
    private createIndexKey(testheftId: number, aufgabeId: number): string {
        return `${testheftId}:${aufgabeId}`;
    }

    /** Liest die Aufgabendaten des Auslösers und baut dessen Informationen auf */
    protected renderTrigger(trigger: HTMLElement): boolean {
        const aufgabeIdRaw = trigger.dataset.tooltipAufgabeId ?? '';
        const testheftIdRaw = trigger.dataset.tooltipTestheftId ?? '';
        const aufgabeId = Number(aufgabeIdRaw);
        const testheftId = Number(testheftIdRaw);

        if (aufgabeIdRaw.trim() === '' || testheftIdRaw.trim() === '' || Number.isInteger(aufgabeId) === false || Number.isInteger(testheftId) === false)
            return false;

        this.render(testheftId, aufgabeId);
        return true;
    }

    /** Baut die sichtbaren Informationen für die angeforderte Aufgabe auf */
    private render(testheftId: number, aufgabeId: number): void {
        // Aufgabendaten aus dem vorbereiteten Index lesen
        const data = this.aufgabeIndex.get(this.createIndexKey(testheftId, aufgabeId)) ?? null;

        // Verständlichen Hinweis anzeigen, falls die Aufgabe nicht im geladenen Testheftbestand vorkommt
        if (data === null) {
            this.content.innerHTML = `
                <strong class="tooltip-aufgabe-title">Aufgabe ${aufgabeId}</strong>
                <p>Für diese Aufgabe sind keine weiteren Informationen verfügbar.</p>
            `;
            return;
        }

        // Domäne und Schwierigkeitsstufe bilden gemeinsam mit der Position die Aufgabenbezeichnung
        const domaene = this.domaenen.find(eintrag => eintrag.domaene === data.modul.domaene) ?? null;
        const domaenenKurzbezeichnung = (data.testheft.fach === 'ma') ? '' : `${domaene?.kurzbezeichnung ?? ''} `;
        const domaenenBezeichnung = (data.testheft.fach === 'ma') ? '' : `${domaene?.bezeichnung ?? ''} `;
        const schwierigkeit = Utilities.printSchwierigkeit(data.testheft.fach, data.modul.schwierigkeit);
        const aufgabePosition = (this.projektweiche === 'vera5')
            ? Utilities.getVera5Aufgabenposition(data.testheft, data.aufgabe.aufgabe_id, data.aufgabe.position_nr)
            : data.aufgabe.position_nr;
        const aufgabeName = (this.projektweiche === 'vera5')
            ? `Aufgabe ${aufgabePosition}`
            : `${domaenenKurzbezeichnung}${data.modul.typ.toUpperCase()} ${schwierigkeit} ${data.aufgabe.position_nr}`.replace(/\s+/g, ' ').trim();
        const modulName = `${domaenenBezeichnung}${Utilities.printModulLang(data.modul.typ)} ${data.aufgabe.position_nr}`.replace(/\s+/g, ' ').trim();
        const kommentar = this.kommentarIndex.get(aufgabeId) ?? null;
        const aufgabeBezeichnung = kommentar?.bezeichnung?.trim() || data.aufgabe.bezeichnung;
        const erstesCbtItemId = data.aufgabe.items[0]?.cbt_item_id?.trim() ?? '';
        const aufgabeBaseAufgId = erstesCbtItemId.replace(/_\d+(?:_\d+)*$/, '');
        const aufgabenansicht = (this.projektweiche === 'vera5' && data.testheft.fach === 'de')
            ? this.renderAufgabenansicht(aufgabeBaseAufgId)
            : '';
        const materialienDomaenenKuerzel = data.aufgabe.domaene || data.modul.domaene;
        const materialienDomaene = this.domaenen.find(eintrag => eintrag.domaene === materialienDomaenenKuerzel) ?? null;
        const materialien = getTooltipMaterialien(this.projektweiche, data.testheft, materialienDomaenenKuerzel);

        // Die global gültigen Informationen der gefundenen Aufgabe als beschriftete Liste ausgeben
        this.content.innerHTML = `
            <strong class="tooltip-aufgabe-title">${aufgabeName}</strong>
            <dl>
                <dt>${this.renderUeberschrift('Testheft', 'journal-bookmark')}</dt>
                <dd>${data.testheft.bezeichnung} (${Utilities.printFachLang(data.testheft.fach)})</dd>

                ${(this.projektweiche === 'vera5') ? '' : `
                <dt>${this.renderUeberschrift('Modul', 'collection')}</dt>
                <dd>${modulName}</dd>`}

                <dt>${this.renderUeberschrift('Aufgabe', 'card-checklist')}</dt>
                <dd>${aufgabeBezeichnung}</dd>
            </dl>

            ${aufgabenansicht}
            ${this.renderDidaktischeInhalte(kommentar?.kommentar_html, kommentar?.anregungen_fuer_unterricht_html)}
            ${renderTooltipDatenhinweis(materialien, 'Aufgaben', materialienDomaene?.bezeichnung)}
        `;
    }

    /** Verbindet eine Tooltip-Überschrift mit einem dekorativen Bootstrap-Icon */
    private renderUeberschrift(bezeichnung: string, icon: string): string {
        return `<svg class="tooltip-ueberschrift-icon" aria-hidden="true" focusable="false"><use href="${BOOTSTRAP_ICON_SPRITE}#${icon}"/></svg><span>${bezeichnung}</span>`;
    }

    /** Rendert den zu einer Deutsch-Aufgabe gehörenden Stimulus mit einem Fallback */
    private renderAufgabenansicht(baseAufgId: string | null): string {
        const normalizedBaseAufgId = baseAufgId?.trim() ?? '';
        if (BASE_AUFG_ID_PATTERN.test(normalizedBaseAufgId) === false) {
            return `
                <details class="tooltip-aufgabenansicht" open>
                    <summary>${this.renderUeberschrift('Aufgabenansicht', 'image')}</summary>
                    <p class="tooltip-aufgabenansicht-hinweis">Keine Aufgabenansicht verfügbar.</p>
                </details>
            `;
        }

        const bildUrl = `${AUFGABENBILD_BASISPFAD}/${encodeURIComponent(normalizedBaseAufgId)}.png`;
        return `
            <details class="tooltip-aufgabenansicht" open>
                <summary>${this.renderUeberschrift('Aufgabenansicht', 'image')}</summary>
                <div class="tooltip-aufgabenansicht-bildbereich">
                    <a class="tooltip-aufgabenansicht-bildlink" href="${bildUrl}" target="_blank" rel="noopener" aria-label="Darstellung der Aufgabe in Originalgröße öffnen" title="In Originalgröße öffnen">
                        <img class="tooltip-aufgabenansicht-bild" src="${bildUrl}" alt="Darstellung der Aufgabe">
                    </a>
                </div>
                <p class="tooltip-aufgabenansicht-hinweis" hidden>Keine Aufgabenansicht verfügbar.</p>
            </details>
        `;
    }

    /** Rendert die umfangreichen didaktischen HTML-Texte in gestuften Detailbereichen */
    private renderDidaktischeInhalte(kommentarHtml: string | null | undefined, anregungenHtml: string | null | undefined): string {
        const kommentar = kommentarHtml?.trim() ?? '';
        const anregungen = anregungenHtml?.trim() ?? '';
        if (kommentar === '' && anregungen === '')
            return '';

        return `
            <div class="tooltip-didaktik">
                ${(kommentar === '') ? '' : `
                <details open>
                    <summary>${this.renderUeberschrift('Didaktischer Kommentar', 'chat-left-text')}</summary>
                    <div class="tooltip-didaktik-html">${kommentar}</div>
                </details>`}
                ${(anregungen === '') ? '' : `
                <details open>
                    <summary>${this.renderUeberschrift('Anregungen für den Unterricht', 'lightbulb')}</summary>
                    <div class="tooltip-didaktik-html">${anregungen}</div>
                </details>`}
            </div>
        `;
    }

}
